import { difference, noop } from "lodash-es"
import * as THREE from "three"

import "event-target-polyfill"

export class ProgressEvent extends Event {
    constructor(
        public readonly loaded: number,
        public readonly total: number
    ) {
        super("progress")
    }
}

interface Loader {
    status: number // 0: empty, 1: busy
    textureLoader: THREE.TextureLoader
    fileLoader: THREE.FileLoader
    imageLoader: THREE.ImageLoader
}

class MainLoader extends EventTarget {
    private static _inst: MainLoader

    static getInst(): MainLoader {
        if (!this._inst) {
            this._inst = new MainLoader(8)
        }
        return this._inst
    }

    private loaders: Loader[] = []

    private manager: THREE.LoadingManager

    private initialList: {
        url: string
        isTexture: boolean
    }[] = []

    private total = 0

    private textureStore: Record<string, THREE.Texture> = {}

    private initiated = false

    private initialOnLoadCbs: (() => void)[] = []

    constructor(private readonly thread: number = 1) {
        super()
        this.manager = new THREE.LoadingManager(
            noop,
            this.handleProgress,
            this.handleError
        )

        if (this.thread <= 0) {
            throw new Error("Thread can't be less than 1 .")
        }

        for (let i = 0; i < this.thread; i++) {
            this.loaders.push({
                status: 0,
                textureLoader: new THREE.TextureLoader(this.manager),
                fileLoader: new THREE.FileLoader(this.manager),
                imageLoader: new THREE.ImageLoader(this.manager),
            })
        }

        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")
        if (!ctx) {
            throw new Error("Can't create canvas")
        }
    }

    private onLoadHandle = () => {
        this.initialOnLoadCbs.forEach((cb) => {
            cb()
        })
        this.dispatchEvent(new Event("complete"))
    }

    private handleProgress = (url: string, loaded: number) => {
        const e = new ProgressEvent(loaded, this.total)
        this.dispatchEvent(e)
    }

    private handleError = (url: string) => {
        console.error(url)
    }

    async load(url: string, isTexture?: boolean): Promise<THREE.Texture>
    async load(url: string[], isTexture?: boolean): Promise<THREE.Texture[]>
    async load(
        url: string | string[],
        isTexture = true
    ): Promise<THREE.Texture | THREE.Texture[]> {
        if (!this.initiated) {
            if (typeof url === "string") {
                if (!this.initialList.some((item) => item.url === url)) {
                    this.initialList.push({
                        url,
                        isTexture,
                    })
                }
            } else {
                const urls = difference(
                    url,
                    this.initialList.map((item) => item.url)
                )

                this.initialList.push(
                    ...urls.map((u) => ({
                        url: u,
                        isTexture,
                    }))
                )
            }

            this.total = this.initialList.length
            return new Promise<THREE.Texture | THREE.Texture[]>((res) => {
                this.initialOnLoadCbs.push(() => {
                    if (typeof url === "string") {
                        res(this.textureStore[url])
                        return
                    }
                    res(url.map((u) => this.textureStore[u]))
                })
            })
        }

        return new Promise<THREE.Texture | THREE.Texture[]>((res) => {
            // TODO implement runtime load

            if (typeof url === "string") {
                this.loaders[0].textureLoader.load(url, (tex) => {
                    res(tex)
                })
            } else {
            }
        })
    }

    startLoadInitial() {
        this.loaders.forEach((i) => {
            this.loadInitialNext(i)
        })
    }

    getTexture(url: string) {
        return this.textureStore[url]
    }

    private async loadInitialNext(i: Loader) {
        if (this.initialList.length === 0) {
            if (!this.initiated && this.loaders.every((i) => i.status === 0)) {
                this.initiated = true
                this.onLoadHandle()
            }
            return
        }

        const { url, isTexture } = this.initialList.splice(0, 1)[0]
        if (this.textureStore[url]) {
            this.loadInitialNext(i)
            return
        }

        i.status = 1

        if (isTexture) {
            const tex = await i.textureLoader.loadAsync(url)
            tex.minFilter = THREE.LinearFilter
            // tex.encoding = THREE.sRGBEncoding
            this.textureStore[url] = tex
        } else if (url.endsWith(".png") || url.endsWith(".jpg")) {
            await i.imageLoader.loadAsync(url)
        } else {
            await i.fileLoader.loadAsync(url)
        }

        i.status = 0
        this.loadInitialNext(i)
        return
    }
}

export default MainLoader
